import axiosClient, { apiGet, apiPatch, apiPost, apiPut } from "./axiosClient";
import type {
  DoctorAvailability,
  DoctorDetail,
  DoctorDocumentDto,
  DoctorDocumentType,
  DoctorSummary,
  PatientProfile,
  PatientProfileUpdate,
  Review,
  Specialization,
} from "@/types/domain";

/** Public. `q` matches name, qualification, bio, or specialty (including words like "skin"). */
export const listDoctors = (params?: {
  specialization?: string;
  city?: string;
  q?: string;
  page?: number;
  size?: number;
}) =>
  apiGet<DoctorSummary[] | { content: DoctorSummary[]; totalElements: number }>(
    "/api/users/doctors",
    { params },
  );

/** Public. Single doctor by UUID. */
export const getDoctor = (id: string) =>
  apiGet<DoctorDetail>(`/api/users/doctors/${id}`);

/** Public. Doctor + availability by Keycloak id. */
export const getDoctorByKeycloakId = (keycloakId: string) =>
  apiGet<DoctorDetail>(`/api/users/doctors/by-keycloak/${keycloakId}`);

/** Public. */
export const listSpecializations = () =>
  apiGet<Specialization[]>("/api/users/specializations");

/** Public. Backend returns a Spring `Page<ReviewDto>`; unwrap `content`. */
export const listDoctorReviews = async (
  doctorId: string,
  params?: { page?: number; size?: number },
): Promise<Review[]> => {
  const res = await apiGet<
    Review[] | { content: Review[]; totalElements?: number }
  >(`/api/users/doctors/${doctorId}/reviews`, { params });
  if (Array.isArray(res)) return res;
  return res?.content ?? [];
};

export const submitReview = (
  doctorId: string,
  body: { rating: number; comment?: string },
) => apiPost<Review>(`/api/users/doctors/${doctorId}/reviews`, body);

/** Patient scope (JWT ROLE_PATIENT). */
export const getPatientProfile = () =>
  apiGet<PatientProfile>("/api/users/patient/profile");

export const updatePatientProfile = (body: PatientProfileUpdate) =>
  apiPut<PatientProfile>("/api/users/patient/profile", body);

/** Doctor scope (JWT ROLE_DOCTOR). */
export const getDoctorProfile = () =>
  apiGet<DoctorDetail>("/api/users/doctor/profile");

export const updateDoctorProfile = (body: Partial<DoctorDetail>) =>
  apiPut<DoctorDetail>("/api/users/doctor/profile", body);

export const updateDoctorAvailability = (availability: DoctorAvailability[]) =>
  apiPut<DoctorAvailability[]>("/api/users/doctor/availability", availability);

export interface SubmitDoctorVerificationBody {
  registrationNumber: string;
  registrationCouncil: string;
  registrationYear?: number;
  licenseDocUrl?: string;
  idProofDocUrl?: string;
  degreeDocUrl?: string;
}

/** Step 2 onboarding: sends registration + document URLs for admin approval. */
export const submitDoctorForReview = (body: SubmitDoctorVerificationBody) =>
  apiPost<DoctorDetail>("/api/users/doctor/profile/submit-for-review", body);

// ── Doctor document endpoints (BLOB upload) ──────────────────────────

export const uploadDoctorDocument = async (
  file: File,
  documentType: DoctorDocumentType,
  notes?: string,
): Promise<DoctorDocumentDto> => {
  const form = new FormData();
  form.append("file", file);
  form.append("documentType", documentType);
  if (notes) form.append("notes", notes);
  const { data } = await axiosClient.post<DoctorDocumentDto>(
    "/api/users/doctor/documents",
    form,
  );
  return data;
};

export const listDoctorDocuments = () =>
  apiGet<DoctorDocumentDto[]>("/api/users/doctor/documents");

export const downloadDoctorDocument = async (documentId: string): Promise<Blob> => {
  const { data } = await axiosClient.get(`/api/users/doctor/documents/${documentId}/download`, {
    responseType: "blob",
  });
  return data;
};

export const deleteDoctorDocument = async (documentId: string): Promise<void> => {
  await axiosClient.delete(`/api/users/doctor/documents/${documentId}`);
};

// ── Admin ──────────────────────────────────────────────────────────────

/** Admin (ROLE_ADMIN). Lists doctors whose verification is awaiting review. */
export const listPendingDoctors = () =>
  apiGet<DoctorDetail[]>("/api/users/admin/doctors/pending");

/** Admin (ROLE_ADMIN). Approves or rejects a specific doctor. */
export const decideDoctor = (
  doctorId: string,
  body: { approve: boolean; reason?: string },
) => apiPatch<DoctorDetail>(`/api/users/admin/doctors/${doctorId}/decision`, body);

/** Admin (ROLE_ADMIN). Lists currently approved (active) doctors. */
export const listApprovedDoctors = () =>
  apiGet<DoctorDetail[]>("/api/users/admin/doctors/approved");

/** Admin (ROLE_ADMIN). Lists currently suspended doctors. */
export const listSuspendedDoctors = () =>
  apiGet<DoctorDetail[]>("/api/users/admin/doctors/suspended");

/** Admin (ROLE_ADMIN). Suspends an approved doctor. */
export const suspendDoctor = (
  doctorId: string,
  body?: { reason?: string },
) => apiPost<DoctorDetail>(`/api/users/admin/doctors/${doctorId}/suspend`, body ?? {});

/** Admin (ROLE_ADMIN). Reactivates a suspended doctor. */
export const reactivateDoctor = (doctorId: string) =>
  apiPost<DoctorDetail>(`/api/users/admin/doctors/${doctorId}/reactivate`, {});

/** Admin (ROLE_ADMIN). Lists uploaded documents for a specific doctor. */
export const listDoctorDocumentsAdmin = (doctorId: string) =>
  apiGet<DoctorDocumentDto[]>(`/api/users/admin/doctors/${doctorId}/documents`);

/** Admin (ROLE_ADMIN). Download a specific document. */
export const downloadDoctorDocumentAdmin = async (documentId: string): Promise<Blob> => {
  const { data } = await axiosClient.get(`/api/users/admin/documents/${documentId}/download`, {
    responseType: "blob",
  });
  return data;
};
